// All survey copy, verbatim. Phase 1.7: em-dashes scrubbed, "report" → "snapshot".

export const QUESTIONS = [
    {
        id: "persona",
        kind: "card",
        tag: "Let's start here.",
        question: "Which of these is actually you right now?",
        subtitle: "No judgment. This shapes everything.",
        options: [
            {
                value: "student",
                icon: "GraduationCap",
                emoji: "🎓",
                title: "Student or fresh grad",
                sub: "Finding direction before the pressure really hits",
            },
            {
                value: "professional",
                icon: "Briefcase",
                emoji: "💼",
                title: "Working professional",
                sub: "I have a career. I'm questioning whether it's the right one.",
            },
            {
                value: "career_services",
                icon: "BookOpenCheck",
                emoji: "🏫",
                title: "Career counsellor or educator",
                sub: "I work with people navigating this every day",
            },
            {
                value: "employer",
                icon: "Building2",
                emoji: "🏢",
                title: "HR, L&D, or talent leader",
                sub: "I develop people for a living",
            },
        ],
    },
    {
        id: "pain",
        kind: "card",
        tag: "The honest bit.",
        question: "What is actually keeping you up at night career-wise?",
        subtitle: "Pick the one that's most true. Not the most impressive.",
        options: [
            {
                value: "direction",
                icon: "Compass",
                emoji: "🌀",
                title: "I genuinely don't know what I want",
                sub: "Too many options, or none of them feel right",
            },
            {
                value: "stuck",
                icon: "MountainSnow",
                emoji: "🧱",
                title: "I know what I want, I just can't get there",
                sub: "The path is unclear or something is blocking it",
            },
            {
                value: "confidence",
                icon: "ShieldQuestion",
                emoji: "🪞",
                title: "I doubt whether I'm actually good enough",
                sub: "Imposter syndrome is a real and exhausting thing",
            },
            {
                value: "external",
                icon: "HeartHandshake",
                emoji: "🏠",
                title: "Family or financial pressure limits my choices",
                sub: "I can't just follow my instinct. The stakes are real.",
            },
            {
                value: "none",
                icon: "Sparkles",
                emoji: "☀️",
                title: "Honestly I'm doing fine",
                sub: "Just curious what Klareet is about",
            },
        ],
    },
    {
        id: "tools",
        kind: "multi",
        tag: "What you've already tried.",
        question: "Which of these have you actually used for career clarity?",
        subtitle: "Select all that apply. We want to know what hasn't worked.",
        options: [
            { value: "linkedin", icon: "Linkedin", emoji: "💻", title: "LinkedIn courses or career assessments" },
            { value: "therapist", icon: "Stethoscope", emoji: "🛋", title: "Career counsellor or coach" },
            { value: "tests", icon: "Brain", emoji: "🧬", title: "MBTI, StrengthsFinder, DISC, or similar" },
            { value: "friends", icon: "Users", emoji: "💬", title: "Mentors, friends, or family conversations" },
            { value: "books", icon: "BookOpen", emoji: "📚", title: "Books or podcasts on careers" },
            { value: "nothing", icon: "CircleSlash", emoji: "🤷", title: "Nothing yet. I'm figuring it out as I go" },
        ],
    },
    {
        id: "gap",
        kind: "card",
        tag: "The part nobody asks.",
        question: "What did those tools actually miss?",
        subtitle: "This is the most important question so far. Be real.",
        options: [
            {
                value: "generic",
                icon: "ClipboardList",
                emoji: "📋",
                title: "Everything felt too generic",
                sub: "Like it was written for everyone so it helped no one",
            },
            {
                value: "no_action",
                icon: "Lightbulb",
                emoji: "💡",
                title: "Interesting insights, zero clear actions",
                sub: "I understood myself better. I still didn't know what to do.",
            },
            {
                value: "not_honest",
                icon: "VenetianMask",
                emoji: "🎭",
                title: "I wasn't honest in my answers",
                sub: "The questions had obvious right answers and I gave them",
            },
            {
                value: "missing_context",
                icon: "Globe2",
                emoji: "🌍",
                title: "They ignored my actual situation",
                sub: "Family expectations, money, geography. All invisible to the tool.",
            },
            {
                value: "worked",
                icon: "BadgeCheck",
                emoji: "✅",
                title: "They actually worked fine for me",
                sub: "I'm here because I'm curious, not because I'm stuck",
            },
        ],
    },
    {
        id: "klareet_appeal",
        kind: "scale",
        tag: "Gut reaction.",
        question:
            "Klareet has a real conversation with you, finds signals you didn't know were there, and gives you a specific plan, not a generic snapshot.",
        subtitle: "How much does that actually appeal to you?",
        lo: "Hard pass",
        hi: "Where do I sign",
        reactions: {
            1: { emoji: "😬", text: "That's fair. Tell us what we're missing." },
            2: { emoji: "🤔", text: "Sceptical. Noted. We respect it." },
            3: { emoji: "😐", text: "We'll need to earn this one." },
            4: { emoji: "🙂", text: "Warming up. Keep going." },
            5: { emoji: "👍", text: "Middling. We want more." },
            6: { emoji: "🤙", text: "Getting interesting." },
            7: { emoji: "😊", text: "Good. That's the target." },
            8: { emoji: "🔥", text: "Now we're talking." },
            9: { emoji: "🚀", text: "This is the energy we're building for." },
            10: { emoji: "👑", text: "We will not let you down." },
        },
    },
    {
        id: "price_individual",
        kind: "card",
        tag: "The money question.",
        question:
            "Full assessment + psychology profile + 90-day plan + weekly accountability check-ins. What would you actually pay per month?",
        subtitle: "Be honest. We know when people are being polite.",
        options: [
            { value: "0", icon: "Gift", emoji: "🆓", title: "Nothing. Free only.", sub: "I wouldn't pay for career tools" },
            { value: "5", icon: "Coffee", emoji: "☕", title: "₹399–499 per month", sub: "Price of one coffee a month" },
            { value: "10", icon: "Tv", emoji: "📱", title: "₹799–999 per month", sub: "Price of a streaming subscription" },
            { value: "20", icon: "CreditCard", emoji: "💳", title: "₹1,499–1,999 per month", sub: "Real money, if it actually works" },
            { value: "30", icon: "Gem", emoji: "💎", title: "₹2,500+ per month", sub: "Career clarity is worth serious investment" },
        ],
    },
    {
        id: "college_price",
        kind: "card",
        tag: "The institution question.",
        question:
            "If your college paid for Klareet for all students, what's a fair per-student price?",
        subtitle: "Think about what career services at your institution is actually worth.",
        options: [
            { value: "0.50", icon: "Coins", emoji: "🪙", title: "Under ₹50 per student", sub: "Volume only" },
            { value: "1", icon: "Bookmark", emoji: "📌", title: "₹90 per student", sub: "Reasonable for an annual tool" },
            { value: "2", icon: "TrendingUp", emoji: "📈", title: "₹180 per student", sub: "Good value if it genuinely helps" },
            { value: "5", icon: "Trophy", emoji: "🏆", title: "₹450 per student", sub: "Worth it if it beats the alternatives" },
            { value: "10", icon: "Target", emoji: "🎯", title: "₹900+ per student", sub: "Career outcomes justify this" },
        ],
    },
    {
        id: "one_thing",
        kind: "text",
        tag: "The real one.",
        question:
            "What is the ONE thing a career tool has never done for you that you actually needed?",
        subtitle:
            "This answer will be read by the people building Klareet. Make it count.",
        placeholder: "Type something specific. Generic gets generic back.",
        maxLength: 2000,
        optional: true,
    },
    {
        id: "waitlist",
        kind: "card",
        tag: "Almost there.",
        question: "Klareet launches Q3 2026. Early access?",
        subtitle:
            "Early users shape the product. That's not marketing. It's how we actually build.",
        options: [
            { value: "yes_pay", icon: "Rocket", emoji: "🔑", title: "Yes, I'd pay for early access", sub: "₹499 for first 3 months" },
            { value: "yes_free", icon: "Hand", emoji: "✋", title: "Yes, if free or heavily discounted", sub: "Happy to give honest, detailed feedback" },
            { value: "maybe", icon: "Clock", emoji: "👀", title: "Maybe, send me more first", sub: "I want to see what you actually build" },
            { value: "no", icon: "CircleX", emoji: "👋", title: "Not right now", sub: "Not the right moment for me" },
        ],
    },
    {
        id: "email",
        kind: "email",
        tag: "Last one.",
        question: "Leave your email if you want to hear from us.",
        subtitle:
            "One email when we launch. One if we have something worth sharing. That's it.",
        optional: true,
    },
];

export const REACTION_TOASTS = {
    pain: {
        direction: "Clarity over confusion. That's exactly what we're building for.",
        stuck: "Knowing what you want and being blocked from it is a very specific kind of frustrating.",
        confidence: "Imposter syndrome is the gap between what you can do and what you believe you can do.",
        external: "External constraints are real. Generic career advice ignores them. We won't.",
        none: "Fair. Curious minds are good minds.",
    },
    gap: {
        generic: "Generic tools are the biggest problem in career development. We agree.",
        no_action: "Insights without actions is just self-awareness theatre. We build for the action.",
        not_honest: "The questions that let you lie are the problem. We design around that.",
        missing_context: "India, UAE, family pressure, geography. All real. All invisible to most tools.",
        worked: "Genuinely happy for you. You're rare.",
    },
};

export const INTENT_CTA_COPY = {
    yes_pay: "You're in. First in line. Early paid access users shape the product. We'll reach out before public launch.",
    yes_free: "You're on the list. Access before the public launch. Your feedback will genuinely matter.",
    maybe: "We'll send you one more thing. No pressure. When it's ready, you'll know what it is.",
    no: "Thanks for your time.",
};

export function isQuestionRequired(q, answers) {
    if (q.optional) return false;
    if (q.id === "college_price") {
        return ["student", "career_services", "employer"].includes(
            answers.persona,
        );
    }
    if (q.id === "gap") {
        return (answers.tools ?? []).length > 0;
    }
    return true;
}

export function shouldSkip(q, answers) {
    if (q.id === "college_price" && answers.persona === "professional") {
        return true;
    }
    if (
        q.id === "gap" &&
        (answers.tools?.length === 0 ||
            (answers.tools?.length === 1 && answers.tools[0] === "nothing"))
    ) {
        return true;
    }
    return false;
}

export const TOTAL_QUESTIONS = QUESTIONS.length;
